import React, { useCallback, useState } from 'react';

import { TreeSelect } from 'primereact/treeselect';
import { Box } from '@mui/material';
import { Icon, Label } from 'semantic-ui-react';
import shortid from 'shortid';
import _ from 'lodash';

import 'primeicons/primeicons.css';

import { OhsFormFieldBaseProps } from 'global-components/form/services/OhsFormModels';
import OhsFormFieldLayout from 'global-components/form/components/OhsFormFieldLayout';
import OhsDragAndDrop from 'global-components/dnd/OhsDragAndDrop';
import DragAndDropSelectItem from 'global-components/dnd/OhsDragAndDropDisplay';

import { SelectFieldOptionType } from './OhsCusvalSelectField';
import OhsSelectValueDisplay from './OhsSelectValueDisplay';

const convertToTreeNodes = (data: any[], parentKey = '0'): any[] => {
  return data.map((item, index) => {
    const key = `${parentKey}-${index}`;

    const label = item.name ?? item.static ?? 'Unnamed';

    const children = item.options ? convertToTreeNodes(item.options, key) : undefined;

    return {
      key,
      label,
      icon: item.icon,
      children,
      selectable: !children || children.length === 0,
    };
  });
};

interface Props extends OhsFormFieldBaseProps {
  options: Array<SelectFieldOptionType>;
  multi?: boolean;
}

function InputEle(props: Props) {
  const disabled = props.disabled || false;
  const inputRef = props.ohsFormSettings.useFormMethods.register(props.id).ref;
  const [selectedNodeKeys, setSelectedNodeKeys] = useState(null);

  const result = props.ohsFormSettings.useFormMethods.watch(props.id) || [];

  const treeData = convertToTreeNodes(props.options);

  const getOptions = () => {
    const unselectedOptions: any[] = [];
    props.options.forEach((option: any) => {
      if (_.isNil((props.options || []).find((item: any) => item === option.value))) {
        unselectedOptions.push(option);
      }
    });

    return unselectedOptions;
  };

  const handleArrange = (list: string[]) => {
    props.ohsFormSettings.useFormMethods.setValue(props.id, [...list]);
  };

  const renderValue = (item: string | string[]) => (
    <OhsSelectValueDisplay value={item} options={getOptions()} />
  );

  const findKeyByNamePath = (names: string[], nodes: any[], prefix = '0') => {
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      const currentKey = `${prefix}-${i}`;

      if (node.name === names[0] || node.static === names[0]) {
        if (names.length === 1) return currentKey;

        if (node.options) {
          const next: any = findKeyByNamePath(names.slice(1), node.options, currentKey);
          if (next) return next;
        }
      }
    }
    return null;
  };

  const deleteSelectedNode = (pathToRemove: string[] | string) => {
    const updated = result.filter((pathArr: string[]) => {
      if (pathArr.length !== pathToRemove.length) return true;
      return !pathArr.every((val, i) => val === pathToRemove[i]);
    });

    const newSelectedKeys: any = {};
    updated.forEach((pathArr: any) => {
      const key = findKeyByNamePath(pathArr, treeData);
      if (key) newSelectedKeys[key] = true;
    });
    setSelectedNodeKeys(newSelectedKeys);
  };

  const handleRemove = (value: string | string[]) => {
    if (value) {
      props.ohsFormSettings.useFormMethods.setValue(props.id, [
        ...result.filter((item: string) => item !== value),
      ]);
      deleteSelectedNode(value);
      props.ohsFormSettings.useFormMethods.trigger(props.id);
    }
  };

  const getSelectedPaths = (selectedKeys: any, options: any[]) => {
    const list: string[][] = [];

    Object.keys(selectedKeys).forEach((path: string) => {
      const indexes = path.split('-').map(Number);
      const names: string[] = [];
      let currentList = options;

      for (let i = 1; i < indexes.length; i += 1) {
        const idx = indexes[i];
        const node = currentList[idx];

        if (!node) break;

        const name = node.name ?? node.static ?? 'Unnamed';
        names.push(name);

        currentList = node.options || [];
      }

      list.push(names);
    });

    return list;
  };

  const handleNodeSelect = (selectedKeys: any) => {
    setSelectedNodeKeys(selectedKeys);
    const selectedNodes: any[] = getSelectedPaths(selectedKeys, props.options);
    props.ohsFormSettings.useFormMethods.setValue(props.id, [...selectedNodes]);
  };

  const renderNodeTemplate = useCallback(
    (node: any) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {node.icon && <Icon name={node.icon} />}
        <span>{node.label}</span>
      </div>
    ),
    []
  );

  React.useEffect(() => {
    props.ohsFormSettings.useFormMethods.register(props.id, {
      validate: {
        notEmpty: () => {
          const value = props.ohsFormSettings.useFormMethods.getValues(props.id) ?? [];
          if (props.required === true) {
            if (value && value.length > 0) {
              return true;
            }
          } else {
            return true;
          }

          return false;
        },
      },
    });
  }, [props.id, props.ohsFormSettings.useFormMethods, props.required, result]);

  return (
    <Box>
      <TreeSelect
        ref={inputRef}
        value={selectedNodeKeys}
        onChange={(e: any) => handleNodeSelect(e.value)}
        options={treeData}
        metaKeySelection={false}
        style={{ width: '100%' }}
        selectionMode={props.multi ? 'multiple' : 'single'}
        placeholder="Please select"
        showClear
        // eslint-disable-next-line react/no-unstable-nested-components
        valueTemplate={() => <span style={{ color: '#6c757d' }}>--Please Select--</span>}
        nodeTemplate={renderNodeTemplate}
      />
      <div data-testid={`${props.id}-values`}>
        {props.multi !== true && result.length > 0 ? (
          result.map((item: string) => (
            <Label key={shortid.generate()} as="a" tag style={{ marginTop: '5px' }}>
              {renderValue(item)}
              {!disabled && (
                <Icon
                  name="delete"
                  onClick={() => {
                    // handleRemove(item);
                  }}
                />
              )}
            </Label>
          ))
        ) : (
          <OhsDragAndDrop
            list={result || []}
            setList={handleArrange}
            withHandle
            renderItem={(item: string | string[]) => (
              <DragAndDropSelectItem
                label={renderValue(item)}
                onDelete={() => handleRemove(item)}
                disabled={disabled}
              />
            )}
          />
        )}
      </div>
    </Box>
  );
}

export default function OhsTreeSelectField(props: Props) {
  return <OhsFormFieldLayout {...props} inputEle={<InputEle {...props} />} />;
}

const defaultProps = {
  multi: false,
};

InputEle.defaultProps = defaultProps;

OhsTreeSelectField.defaultProps = defaultProps;
